import {Component, DestroyRef, inject} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {DomSanitizer, SafeHtml} from '@angular/platform-browser';
import {MAT_DIALOG_DATA, MatDialogModule, MatDialogRef} from '@angular/material/dialog';
import {MatButtonModule} from '@angular/material/button';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {environment} from '../../../../environments/environment';
import type {AgentEmailPreview} from './agent-email-preview-dialog.component';
import {emailPreviewHtml} from './email-preview-html';

export type BulkEmailItem = Partial<Omit<AgentEmailPreview, 'review_token'>> & {activity_id: number; errors: string[]; kind?: 'agent' | 'client' | 'mail'; contacts?: {id: number; name: string; email: string; url: string}[]; clients?: {id: number; name: string; url: string}[]; requests?: {id: number; code: string; url: string}[]; correction_url?: string};
export interface BulkEmailPreview {
  selected_count: number; email_count: number; other_count: number; items: BulkEmailItem[];
  can_confirm: boolean; review_required: boolean; review_token: string | null;
  force_available?: boolean; force_token?: string | null; ready_count?: number; blocked_count?: number;
  ready_ids?: number[]; blocked_ids?: number[]; save_without_automation?: boolean;
}

@Component({
  selector: 'app-bulk-activity-email-preview-dialog',
  imports: [MatDialogModule, MatButtonModule],
  template: `
    <h2 mat-dialog-title>Review Mail recipients</h2>
    <mat-dialog-content>
      <p>Selected Activities: {{ preview.selected_count }} · Emails: {{ preview.email_count }}
        @if (preview.other_count) { · Other Activities: {{ preview.other_count }} }</p>
      <p>Review clients, contacts and To / CC / BCC before saving Ready to do.</p>
      @if (preview.blocked_count) {
        <p class="error" role="alert">{{ preview.blocked_count }} Activities have missing or incorrect recipients, require approval, or cannot be sent.</p>
      }
      @if (forceStep) {
        <section class="warning" role="alert" aria-label="Confirm Force Send">
          <h3>Confirm Force Send</h3>
          <p>Only {{ preview.ready_count }} eligible Mail Activities will be saved as Ready to do.</p>
          <p>These Activities will stay unchanged: {{ preview.blocked_ids?.join(', ') }}.</p>
          @if (noSend) { <strong>DEV: no emails or workflows will run.</strong> }
          <button type="button" mat-stroked-button (click)="forceStep = false">Back</button>
          <button type="button" mat-raised-button color="primary" (click)="confirmForce()">Confirm Force Send</button>
        </section>
      }
      @if (busy) { <p role="status">Refreshing previews…</p> }
      @if (error) { <p role="alert" class="error">{{ error }}</p> }
      @if (blockedItems.length) {
        <section aria-label="Missing or incorrect email recipients" class="error-list">
          <h3>Needs attention</h3>
          @for (item of blockedItems; track item.activity_id) {
            <p>Activity #{{ item.activity_id }}: {{ item.errors.join('; ') }}
              @if (item.correction_url) { <a [href]="item.correction_url" target="_blank" rel="noopener noreferrer">Open email field ↗</a> }
            </p>
          }
        </section>
      }
      @for (item of preview.items; track item.activity_id; let i = $index) {
        <section class="email" [class.blocked-mail-row]="item.errors.length > 0 || item.can_confirm === false" [attr.aria-label]="'Activity ' + item.activity_id">
          <h3>{{ i + 1 }}. {{ item.kind === 'client' ? 'Client prices' : item.kind === 'agent' ? 'Agent quotation' : 'Mail' }} ·
            <a [href]="'/dashboard/form/activities/' + item.activity_id" target="_blank" rel="noopener noreferrer">Activity #{{ item.activity_id }}</a>
          </h3>
          @if (item.clients?.length) {
            <p>Client(s): @for (client of item.clients; track client.id) { <a [href]="client.url" target="_blank" rel="noopener noreferrer">{{ client.name }}</a> }</p>
          }
          @if (item.requests?.length) {
            <p>Request(s): @for (request of item.requests; track request.id) { <a [href]="request.url" target="_blank" rel="noopener noreferrer">{{ request.code }}</a> }</p>
          }
          @for (contact of item.contacts; track contact.id) {
            <p>Contact: {{ contact.name }} · {{ contact.email || 'No email' }} · <a [href]="contact.url" target="_blank" rel="noopener noreferrer">Open email field ↗</a></p>
          }
          @if (item.review_context; as context) {
            <p>{{ context.request.code }} · {{ context.client.name }}</p>
          }
          @if (item.price_approval_required) {
            <p class="warning">{{ item.price_approval?.approved ? 'Price personally approved by Kristine.' : 'Kristine must personally approve this price before saving Ready to do. Open the Activity to review the approval.' }}</p>
            @for (requirement of item.price_approval_requirements; track $index) {
              <p>{{ requirement.source }}: {{ requirement.name }}</p>
            }
          }
          @if (item.subject !== undefined) {
            <dl><dt>From</dt><dd>{{ item.from }}</dd><dt>To</dt><dd>{{ item.to?.join(', ') || '—' }}</dd>
              <dt>CC</dt><dd>{{ item.cc?.join(', ') || '—' }}</dd><dt>BCC</dt><dd>{{ item.bcc?.join(', ') || '—' }}</dd>
              <dt>Subject</dt><dd>{{ item.subject }}</dd></dl>
          }
          @for (warning of item.warnings; track $index) { <p class="warning">{{ warning }}</p> }
          @for (message of item.errors; track $index) { <p class="error" role="alert">{{ message }}</p> }
          @if (html[item.activity_id]) {
            <iframe [title]="'Email body for Activity ' + item.activity_id" sandbox="" referrerpolicy="no-referrer" [srcdoc]="html[item.activity_id]"></iframe>
          }
        </section>
      }
      @if (!preview.can_confirm) { <p class="error" role="alert">Resolve the issues above, then refresh the previews. Nothing has been saved.</p> }
      @if (preview.can_confirm) {
        @if (noSend) { <p>No email will be sent on DEV.</p> }
        @else { <p>Confirming saves Ready to do. The automatic sender may process these emails afterward.</p> }
      }
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button type="button" mat-stroked-button (click)="dialog.close()">Back to edit</button>
      <button type="button" mat-stroked-button [disabled]="busy || confirmed" (click)="refresh()">Refresh</button>
      @if (preview.force_available) {
        <button type="button" mat-stroked-button [disabled]="busy || confirmed || !!error || forceStep" (click)="startForce()">Force Send</button>
      }
      <button type="button" mat-raised-button color="primary" [disabled]="!canConfirm || forceStep" (click)="confirm()">Confirm and save all</button>
    </mat-dialog-actions>
  `,
  styles: `
    :host { display:block; } mat-dialog-actions { gap:8px; }
    .email { border:1px solid #d9e5e4; padding:16px; margin:16px 0; border-radius:6px; }
    .email.blocked-mail-row { background:#ffebee; border-color:#c62828; }
    .error-list { background:#ffebee; border-left:4px solid #c62828; padding:12px; }
    h3 { margin:0 0 12px; } a { color:#007e78; text-decoration:underline; }
    dl { display:grid; grid-template-columns:70px minmax(0,1fr); gap:8px 12px; }
    dt { font-weight:600; } dd { margin:0; overflow-wrap:anywhere; white-space:pre-wrap; }
    iframe { width:100%; height:320px; border:1px solid #d9e5e4; background:white; }
    .warning { padding:10px; background:#fff8de; } .error { color:#a40000; }
  `,
})
export class BulkActivityEmailPreviewDialogComponent {
  readonly dialog = inject(MatDialogRef<BulkActivityEmailPreviewDialogComponent>);
  private readonly data = inject<{preview: BulkEmailPreview; request: object}>(MAT_DIALOG_DATA);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly http = inject(HttpClient);
  private readonly destroyRef = inject(DestroyRef);
  preview = this.data.preview;
  html: Record<number, SafeHtml> = {};
  busy = false;
  confirmed = false;
  error = '';
  forceStep = false;
  get blockedItems(): BulkEmailItem[] { return this.preview.items.filter(item => item.errors.length > 0 || item.can_confirm === false); }

  constructor() { this.render(); }

  get noSend(): boolean { return this.preview.save_without_automation === true || this.preview.items.length > 0 && this.preview.items.every(item => item.save_without_automation); }
  get canConfirm(): boolean {
    return !this.busy && !this.confirmed && !this.error && this.preview.can_confirm && !!this.preview.review_token
      && this.preview.items.every(item => item.can_confirm && item.errors.length === 0
        && (!item.price_approval_required || item.price_approval?.approved));
  }

  refresh(): void {
    if (this.busy || this.confirmed) return;
    this.busy = true; this.error = ''; this.forceStep = false;
    this.preview = {...this.preview, can_confirm: false, review_token: null};
    this.http.post<{data: BulkEmailPreview}>(environment.baseApi + 'entity/search', this.data.request)
      .pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: ({data}) => { this.preview = data; this.render(); this.busy = false; },
        error: () => { this.busy = false; this.error = 'Could not refresh the previews. Check your access and try again. Nothing was saved.'; },
      });
  }

  startForce(): void {
    if (!this.busy && !this.confirmed && !this.error && this.preview.force_available && this.preview.force_token) this.forceStep = true;
  }

  confirmForce(): void {
    if (!this.forceStep || this.busy || this.confirmed || this.error || !this.preview.force_token || !this.preview.force_available) return;
    this.confirmed = true;
    this.dialog.close({token: this.preview.force_token, force: true});
  }

  confirm(): void {
    if (!this.canConfirm) return;
    this.confirmed = true;
    this.dialog.close(this.preview.review_token);
  }

  private render(): void {
    this.html = Object.fromEntries(this.preview.items.filter(item => typeof item.html === 'string')
      .map(item => [item.activity_id, emailPreviewHtml(this.sanitizer, item.html!)]));
  }
}
